import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import { shopActions } from "../../redux/shop/shop.reducer";

import {
  selectCurrentCollectionItems,
  selectIsCollectionItemsFetching,
  selectCurrentCollection,
  selectIsCollectionFetching,
  selectCurrentBrand,
  selectIsBrandFetching,
} from "../../redux/shop/shop.selectors";

import useQuery from "../../hooks/useQuery";

import useStyles from "./collection.styles";

import PetShopPage from "../pet-shop";
import SpecialShopPage from "../special-shop";

import CollectionItem from "../../components/collection-item";
import Loading from "../../components/loading";
import Filter from "../../components/filter";
import Sort from "../../components/sort";

const fetchCollection = (dispatch: any, currentCollectionId: string | null) => {
  dispatch({
    type: shopActions.fetchCollectionStart.type,
    payload: currentCollectionId,
  });
};

const fetchBrand = (dispatch: any, currentBrandId: string | null) => {
  dispatch({
    type: shopActions.fetchBrandStart.type,
    payload: currentBrandId,
  });
};

const emptyCurrentCollection = (dispatch: any) => {
  dispatch({
    type: shopActions.emptyCurrentCollection.type,
  });
};

const emptyCurrentBrand = (dispatch: any) => {
  dispatch({
    type: shopActions.emptyCurrentBrand.type,
  });
};

const fetchCollectionItems = (
  dispatch: any,
  {
    currentCollectionId,
    filterTerms,
    extraFilterTerms,
    sortBy,
  }: {
    currentCollectionId: string | null;
    filterTerms?: any;
    extraFilterTerms?: any;
    sortBy?: string;
  },
) => {
  dispatch({
    type: shopActions.fetchCollectionItemsStart.type,
    payload: {
      collectionIdList: [currentCollectionId],
      filters:
        filterTerms && Object.keys(filterTerms).length !== 0
          ? filterTerms
          : null,
      extraFilters:
        extraFilterTerms && Object.keys(extraFilterTerms).length !== 0
          ? extraFilterTerms
          : null,
      sortBy: sortBy ? sortBy : null,
    },
  });
};

const fetchBrandItems = (
  dispatch: any,
  {
    searchKeyword,
    filterTerms,
    extraFilterTerms,
    sortBy,
  }: {
    searchKeyword: string;
    filterTerms?: any;
    extraFilterTerms?: any;
    sortBy?: string;
  },
) => {
  dispatch({
    type: shopActions.fetchBrandItemsStart.type,
    payload: {
      searchKeyword,
      searchTitle: "BRAND",
      onlyActive: "true",
      onlyCollectionExists: "false",
      filters:
        filterTerms && Object.keys(filterTerms).length !== 0
          ? filterTerms
          : null,
      extraFilters:
        extraFilterTerms && Object.keys(extraFilterTerms).length !== 0
          ? extraFilterTerms
          : null,
      sortBy: sortBy ? sortBy : null,
    },
  });
};

const fetchFilterWithCount = (
  dispatch: any,
  currentCollectionId: string | null,
) => {
  dispatch({
    type: shopActions.fetchFilterWithCountStart.type,
    payload: currentCollectionId,
  });
};

const fetchFilterForBrand = (dispatch: any, brandName: string | null) => {
  dispatch({
    type: shopActions.fetchFilterForBrandStart.type,
    payload: brandName,
  });
};

interface SortElementType {
  label: string;
  value: string;
}

const CollectionPage = () => {
  const dispatch = useDispatch();
  const query = useQuery();
  const location = useLocation();

  const matches = useMediaQuery("(min-width:1200px)");

  const [currentCollectionId, setCurrentCollectionId] = useState(
    query.has("collectionId") ? query.get("collectionId") : "",
  );
  const [currentBrandId, setCurrentBrandId] = useState(
    query.has("brandId") ? query.get("brandId") : "",
  );

  const currentCollection = useSelector(selectCurrentCollection);
  const currentBrand = useSelector(selectCurrentBrand);
  const currentCollectionItems = useSelector(selectCurrentCollectionItems);
  const isCollectionFetching = useSelector(selectIsCollectionFetching);
  const isBrandFetching = useSelector(selectIsBrandFetching);
  const isCollectionItemsFetching = useSelector(
    selectIsCollectionItemsFetching,
  );

  const [filterTerms, setFilterTerms] = useState<{
    [name: string]: { selectedOptions: any[] };
  }>({});
  const [extraFilterTerms, setExtraFilterTerms] = useState<{
    [name: string]: { selectedOptions: any[] };
  }>({});

  const [filterByQuery, setFilterByQuery] = useState(
    query.has("filterBy") ? query.get("filterBy") : "",
  );

  const [sortByQuery, setSortByQuery] = useState(
    query.has("sortBy") ? query.get("sortBy") : "",
  );

  const [selectedSortMethod, setSelectedSortMethod] = useState(
    sortByQuery ? sortByQuery : "",
  );

  let sortElements: SortElementType[] = [
    { label: "Newest", value: "NEWEST" },
    { label: "Bestselling", value: "BESTSELLING" },
    { label: "Price: Low to High", value: "PRICE_LOW_TO_HIGH" },
    { label: "Price: High to Low", value: "PRICE_HIGH_TO_LOW" },
    { label: "Top Rated", value: "TOP_RATED" },
  ];

  useEffect(() => {
    if (sortByQuery) {
      setSelectedSortMethod(sortByQuery);
    }
  }, [location]);

  useEffect(() => {
    setSortByQuery(selectedSortMethod);
  }, [selectedSortMethod]);

  useEffect(() => {
    if (sortByQuery) {
      query.set("sortBy", sortByQuery);
    } else if (sortByQuery === "") {
      query.delete("sortBy");
    }

    if (filterByQuery) {
      query.set("filterBy", filterByQuery);
    } else if (filterByQuery === "") {
      query.delete("filterBy");
    }

    history.replaceState({}, "", `${location.pathname}?${query.toString()}`);
  }, [filterByQuery, sortByQuery]);

  useEffect(() => {
    setCurrentCollectionId(
      query.has("collectionId") ? query.get("collectionId") : "",
    );

    setCurrentBrandId(query.has("brandId") ? query.get("brandId") : "");

    if (!query.has("productId")) {
      dispatch({
        type: shopActions.emptyCurrentItem.type,
      });
    }

    if (!query.has("collectionId")) {
      emptyCurrentCollection(dispatch);
    }

    if (!query.has("brandId")) {
      emptyCurrentBrand(dispatch);
    }
  }, [location.search]);

  useEffect(() => {
    setIsLoading(true);
    setFilterByQuery("");
    setFilterTerms({});
    setExtraFilterTerms({});

    if (query.has("collectionId")) {
      fetchCollection(dispatch, currentCollectionId);
      if (!isItemsLoading) {
        fetchCollectionItems(dispatch, {
          currentCollectionId,
          filterTerms,
          extraFilterTerms,
        });
      }
      fetchFilterWithCount(dispatch, currentCollectionId);
    }
  }, [currentCollectionId]);

  useEffect(() => {
    setIsLoading(true);
    setFilterByQuery("");
    setFilterTerms({});
    setExtraFilterTerms({});

    if (query.has("brandId")) {
      fetchBrand(dispatch, currentBrandId);
    }
  }, [currentBrandId]);

  useEffect(() => {
    if (query.has("brandId") && currentBrand?.brandName) {
      if (!isItemsLoading) {
        fetchBrandItems(dispatch, { searchKeyword: currentBrand.brandName });
      }
      fetchFilterForBrand(dispatch, currentBrand.brandName);
    }
  }, [currentBrand]);

  const [isLoading, setIsLoading] = useState(true);
  const [isCollectionLoading, setIsCollectionLoading] = useState(false);
  const [isItemsLoading, setIsItemsLoading] = useState(false);

  useEffect(() => {
    setIsCollectionLoading(isCollectionFetching || isBrandFetching);
    setIsItemsLoading(isCollectionItemsFetching);
  }, [isCollectionFetching, isBrandFetching, isCollectionItemsFetching]);

  const [filterOpen, setFilterOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);

  const classes = useStyles({ matches });

  // handle filter, sort toggle
  const handleFilterOrSortToggle = (e: any) => {
    const { name } = e.target;
    switch (name) {
      case "filter":
        setFilterOpen(!filterOpen);
        setSortOpen(false);
        break;
      case "sort":
        setSortOpen(!sortOpen);
        setFilterOpen(false);
        break;
      default:
        break;
    }
  };

  const handleReload = (event: any) => {
    event.preventDefault();
    setIsLoading(true);
  };

  // close filter and sort whenever location changes
  useEffect(() => {
    setFilterOpen(false);
    setSortOpen(false);
    setIsLoading(true);
  }, [location.pathname, location.search]);

  useEffect(() => {
    window.addEventListener("load", handleReload);

    return window.removeEventListener("load", handleReload);
  }, []);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [currentCollectionItems]);

  return (
    <div>
      {isLoading && isCollectionLoading ? (
        <Loading on size="3rem" fullScreen />
      ) : currentCollection?.parentCollectionList.length === 1 ? (
        <PetShopPage petTypePath={currentCollection?.path} />
      ) : currentCollection?.path === "special" ? (
        <SpecialShopPage
          collectionItems={currentCollectionItems}
          petTypePath=""
        />
      ) : (
        <div className={classes.container}>
          {query.has("brandId") ? (
            <div className={classes.categories}>
              <span>Shop by Brand</span>
              <svg
                width="24"
                height="24"
                xmlns="http://www.w3.org/2000/svg"
                fillRule="evenodd"
                clipRule="evenodd"
                viewBox="-20 -20 60 60"
              >
                <path d="M4 .755l14.374 11.245-14.374 11.219.619.781 15.381-12-15.391-12-.609.755z" />
              </svg>
              <span>{currentBrand?.brandName}</span>
            </div>
          ) : null}
          <div className={classes.title}>
            {currentBrand
              ? currentBrand.brandName
                  ?.replaceAll("-", " ")
                  .replaceAll("AND", "&")
              : null}
            {currentCollection
              ? currentCollection?.name
                  ?.replaceAll("-", " ")
                  .replaceAll("AND", "&")
              : null}
          </div>
          <div className={classes.functionBar}>
            <button
              name="filter"
              onClick={handleFilterOrSortToggle}
              className={classes.toggleBtn}
            >
              Filter
            </button>
            <button
              name="sort"
              onClick={handleFilterOrSortToggle}
              className={classes.toggleBtn}
            >
              Sort By{" "}
              {sortElements
                .find((el) => el.value === selectedSortMethod)
                ?.label.toUpperCase()}
            </button>
          </div>
          <div className={classes.contentContainer}>
            <Filter
              open={filterOpen}
              filterByQuery={filterByQuery}
              setFilterByQuery={setFilterByQuery}
              sortByQuery={sortByQuery}
              filterTerms={filterTerms}
              setFilterTerms={setFilterTerms}
              extraFilterTerms={extraFilterTerms}
              setExtraFilterTerms={setExtraFilterTerms}
              fetchCollectionItems={fetchCollectionItems}
              fetchBrandItems={fetchBrandItems}
              currentBrand={currentBrand}
            />
            {isItemsLoading ? (
              <Loading on marginTop="3rem" size="3rem" />
            ) : currentCollectionItems?.length !== 0 ? (
              <div className={classes.collectionContainer}>
                {currentCollectionItems?.map((item: any) => (
                  <CollectionItem key={item.dashboardCollection} item={item} />
                ))}
              </div>
            ) : (
              <div className={classes.noItemsFoundContainer}>
                No Items Found
              </div>
            )}
            <Sort
              open={sortOpen}
              setOpen={setSortOpen}
              sortElements={sortElements}
              selectedSortMethod={selectedSortMethod}
              setSelectedSortMethod={setSelectedSortMethod}
              filterTerms={filterTerms}
              extraFilterTerms={extraFilterTerms}
              fetchCollectionItems={fetchCollectionItems}
              fetchBrandItems={fetchBrandItems}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CollectionPage;
